import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { RefreshCw } from 'lucide-react';
import { initiateRenewal } from '@/services/deals.service.js';
import { ApiError } from '@/lib/api.js';

// Initiate renewal on a hangar or property deal (H&H AMG, 2026-10-22). The
// aviation sheet (PreStageRenewalSheet) edits aircraft and pilots and its
// renewal emails the client about them; a property policy has neither. This
// asks for the effective date only. The server clones the deal as a Renewal
// Business deal linked to the expiring one and sends nothing to anyone; the
// broker then logs the carrier's renewal quote on the new deal, selects it,
// and sends the payment request.
export default function PropertyRenewalDialog({ open, onOpenChange, dealId, accountId, initialAccountName, initialEffective, onRenewed }) {
  const navigate = useNavigate();
  const [effective, setEffective] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setEffective(String(initialEffective || '').slice(0, 10));
  }, [open, initialEffective]);

  async function handleStart() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effective)) { toast.error('Effective date is required'); return; }
    setSubmitting(true);
    try {
      // autoSendInvite false: there is no client invite for a property renewal.
      const res = await initiateRenewal(dealId, { effectiveDate: effective, autoSendInvite: false });
      if (!res?.success || !res.deal_id) throw new Error(res?.error || 'Renewal was not created');
      toast.success(`Renewal created: ${res.deal_name}. Log the carrier's renewal quote on it next.`);
      onRenewed?.();
      onOpenChange?.(false);
      if (accountId) navigate(`/account/${accountId}/deal/${res.deal_id}`);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      if (body?.error === 'not_ready') toast.error(`Not ready to renew. Missing: ${(body.missing || []).join(', ')}`);
      else toast.error(body?.error || err.message || 'Could not start the renewal');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange?.(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={18} className="text-accent" /> Initiate renewal
          </DialogTitle>
          <DialogDescription>
            Creates the renewal deal for {initialAccountName || 'this policy'}, linked to the expiring term. Nothing is emailed to the client.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 my-2">
          <div>
            <label className="text-xs font-medium text-text-muted block mb-1">Renewal effective date</label>
            <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
          </div>
          <p className="text-xs text-text-muted">
            Next, on the new deal: log the carrier's renewal quote, select it, then send the payment request.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting}>Cancel</Button>
          <Button type="button" onClick={handleStart} disabled={submitting || !effective}>
            {submitting ? 'Creating...' : 'Create renewal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
